import Grid from '@material-ui/core/Grid';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import FieldSelect from '@react-form-fields/material-ui/components/Select';
import { dateFormat, dateParse } from 'formatters/date';
import { getStatusLabel, statusesColors } from 'interfaces/dashboard';
import { statuses } from 'interfaces/file';
import React, { memo, useState } from 'react';
import {
  Bar,
  BarChart,
  BarProps,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import Card from '../Component/Card';

interface IProps {
  metrics: any[];
}

const useStyles = makeStyles((theme) => ({
  table: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.spacing(1),
    padding: theme.spacing(2),
  },
  content: {
    alignSelf: 'center',
  },
  middleText: theme.typography.h5,
  iconStatus: {
    verticalAlign: 'middle',
  },
  label: {
    ...theme.typography.h6,
    fill: '#000000',
    opacity: 0.6,
  },
  select: {
    borderRadius: 4,
    position: 'relative',
    backgroundColor: theme.palette.secondary.light,
    color: theme.palette.primary.light,
    fontSize: 16,
    padding: '6px 26px 6px 12px',
    transition: theme.transitions.create(['border-color', 'box-shadow']),
    '&:focus': {
      borderRadius: 4,
      borderColor: '#80bdff',
      boxShadow: '0 0 0 0.2rem rgba(0,123,255,.25)',
    },
  },
  typography: {
    fontWeight: 'bold',
    color: '#fff',
  },
}));

const HistoricStatus = memo<IProps>((props: IProps) => {
  const { metrics } = props;
  const classes = useStyles();
  const [statusFilter, setStatusFilter] = useState<statuses[]>([
    statuses.APPROV,
    statuses.EXPIRED,
    statuses.DISAPPROV,
  ]);
  const statusOptions = [...Object.keys(statuses), 'CONSOLIDATED'].map((s) => ({
    value: s,
    label: getStatusLabel(s),
  }));

  const getBarProps = (status: statuses): BarProps => ({
    dataKey: status,
    stroke: statusesColors[status],
    strokeWidth: 3,
    fill: statusesColors[status],
  });

  const handleChangeStatus = (value: statuses[]) => {
    setStatusFilter(value);
  };

  const formatterTicker = (date: string) =>
    dateFormat(dateParse(date, 'yyyy-MM'), 'MMM/yy');

  return (
    <Card style={{ backgroundColor: '#636565' }}>
      <Grid container justify="space-between" spacing={1}>
        <Grid item>
          <Typography className={classes.typography} variant="subtitle1">
            Histórico
          </Typography>
        </Grid>
        <Grid item xs={4}>
          <FieldSelect
            value={statusFilter}
            options={statusOptions}
            onChange={handleChangeStatus}
            classes={{ select: classes.select }}
            fullWidth
            margin="none"
            multiple
          />
        </Grid>
        <Grid item xs={12}>
          <div className={classes.table}>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart height={150} data={metrics}>
                <YAxis axisLine={false} tickLine={false} width={35} />
                <XAxis
                  dataKey="month"
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={formatterTicker}
                />
                <Tooltip
                  labelFormatter={formatterTicker}
                  formatter={(value, name) => [
                    'CONSOLIDATED' === name
                      ? Number(value).toFixed(2) + '%'
                      : value,
                    getStatusLabel(name, true),
                  ]}
                />
                <Legend formatter={(value) => getStatusLabel(value, true)} />
                {statusFilter.map((i) => (
                  <Bar key={`line-${i}`} {...getBarProps(i)} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Grid>
      </Grid>
    </Card>
  );
});

export default HistoricStatus;
